/**
 * AdvisorPortalPage — M11b-2 Konto-Seite „Steuerberater".
 *
 * Bündelt die Steuerberater-Verwaltung, die zuvor im {@link TenantSwitcher}-
 * Dropdown saß, auf einer dedizierten Konto-Seite unter
 * `/dashboard/konto/steuerberater`:
 *  - „Ausstehende Einladungen": rendert {@link PendingInvitesPanel} (Annehmen/
 *    Ablehnen offener Berater-Einladungen, aus M11b-1) — für JEDEN User sichtbar.
 *  - „Steuerberater einladen": eine Platzhalter-/Shell-Sektion. Die komfortable
 *    E-Mail-Einladung wird in M11b-4 hier eingehängt; ein UID-Eingabefeld gibt es
 *    bewusst NICHT mehr (User-Direktive).
 *
 * Der Switcher dient nach M11b-2 wieder ausschließlich dem reinen Mandanten-
 * Wechsel (sichtbar erst ab >1 Tenant). Diese Seite ist die neue Heimat der
 * Berater-Verwaltung und ist über das Konto-Dropdown im Header erreichbar.
 *
 * @module AdvisorPortalPage
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconAdvisor } from '../icons';
import { useAuth } from '../context/AuthContext';
import PendingInvitesPanel from '../components/tenant/PendingInvitesPanel';
import AdvisorInvitePanel from '../components/tenant/AdvisorInvitePanel';
import AdvisorInviteAcceptPanel from '../components/tenant/AdvisorInviteAcceptPanel';
import AdvisorReconfirmBanner from '../components/tenant/AdvisorReconfirmBanner';
import AdvisorAccessPanel from '../components/tenant/AdvisorAccessPanel';
import SentInvitesPanel from '../components/tenant/SentInvitesPanel';
import { ADVISOR_EMAIL_INVITE_ENABLED } from '../api/advisorInviteApi';
import { canInviteAdvisor } from '../config/advisorInviteAccess';

/**
 * Seiten-Wrapper für die Steuerberater-Verwaltung (Pending-Annahme + Invite-Shell).
 *
 * Liest {@link useAuth}.`pendingInvites`/`activeRole`, um den Leerzustand der
 * Pending-Sektion sauber darzustellen und die Invite-Shell nur außerhalb der
 * aktiven Berater-Sicht zu zeigen. Fail-safe: greift ausschließlich auf bereits
 * fail-safe geladene Context-Werte zu und kann selbst nicht werfen.
 */
const AdvisorPortalPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { pendingInvites, activeRole, currentUser, sellerData } = useAuth();

  /**
   * Incrementing key passed to SentInvitesPanel and used as trigger for refresh.
   * After a successful invite, onInviteSent increments this, causing the panel to
   * re-fetch the sent-invites list (M11b-5).
   */
  const [refreshKey, setRefreshKey] = useState(0);

  // Die Invite-Shell ist für Owner gedacht; ein aktiver Berater (read-only-Sicht)
  // bekommt sie nicht zu sehen. M11b Portal-Fix (Bug 4) / WL-K8: zusätzlich Loop-B-
  // Beta-gated (Seed-Test-Account ODER graph-abgeleitet via sellerData.loopBBeta) —
  // ein normaler Owner sieht auch bei Direktaufruf der Route KEIN Einlade-Formular
  // und keinen „in Vorbereitung"-Platzhalter (kontrollierte Owner-Einlade-Beta).
  const showInviteShell = activeRole !== 'advisor' && canInviteAdvisor(currentUser?.uid, sellerData);
  const hasPending = pendingInvites.length > 0;

  return (
    <div className="min-h-screen bg-surface p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-primary flex items-center gap-3">
            <IconAdvisor size={20} className="text-accent-ink" />
            {t('tenant.portalTitle')}
          </h1>
          <p className="text-muted text-sm mt-1">{t('tenant.portalSubtitle')}</p>
        </div>

        {/* §5-Re-Confirm-Hinweis (B4): nur im Advisor-Kontext bei veralteter
            Onboarding-Version + freigegebener §5-Datei; sonst inert (null). */}
        <AdvisorReconfirmBanner />

        {/* Sektion: Token-Einladung annehmen (nur bei ?invite=<token> in der URL +
            Flag on; self-gating, rendert sonst nichts). M11b-4. */}
        <AdvisorInviteAcceptPanel />

        {/* Sektion: Ausstehende Einladungen */}
        <section className="mb-6 rounded-lg bg-surface-raised ring-1 ring-black ring-opacity-5">
          <div className="px-4 pt-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
              {t('tenant.pendingTitle')}
            </h2>
          </div>
          {hasPending ? (
            <PendingInvitesPanel />
          ) : (
            <p className="px-4 py-4 text-sm text-faint">{t('tenant.portalNoPending')}</p>
          )}
        </section>

        {/* Sektion: Zugriff der Kanzleien widerrufen (B5, §7.5). Für JEDEN Owner
            sichtbar — der §3-Consent-Text verspricht den Widerruf „in den
            Einstellungen"; Owner ohne aktive Advisor-Kanten sehen einen
            Leerzustand (inert für die meisten Nutzer). */}
        <AdvisorAccessPanel />

        {/* Sektion: Steuerberater einladen. Bei Flag on (M11b-4) die echte E-Mail-
            Einladung; sonst bleibt der M11b-2-Platzhalter „in Vorbereitung". Ein
            aktiver Berater (read-only-Sicht) sieht keine Einlade-Sektion. */}
        {showInviteShell && (
          <section className="rounded-lg bg-surface-raised ring-1 ring-black ring-opacity-5 p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted mb-2">
              {t('tenant.portalInviteSection')}
            </h2>
            {ADVISOR_EMAIL_INVITE_ENABLED ? (
              <>
                <AdvisorInvitePanel onInviteSent={() => setRefreshKey((k) => k + 1)} />
                <SentInvitesPanel refreshKey={refreshKey} />
              </>
            ) : (
              <>
                <p className="text-sm text-secondary leading-relaxed">
                  {t('tenant.invitePlaceholder')}
                </p>
                <p className="mt-3 inline-flex items-center rounded-md bg-surface-inset px-3 py-1 text-xs font-medium text-secondary">
                  {t('tenant.invitePlaceholderBadge')}
                </p>
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default AdvisorPortalPage;
